// ./src/pages/private/VenuesPage/VenuesPage.tsx
import React, { useState, useCallback } from "react";
//import { useNavigate } from "react-router-dom";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import { VenueFormModal, VenuesTable } from "@/components/features";
import { useVenuesContext } from "@/contexts";
import { Venue } from "@/utils/api";

export const VenuesPage: React.FC<PageProps> = (props: PageProps) => {
  //const navigate = useNavigate();

  // Use the venues context for all data management
  const {
    // Data state
    venues,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    //filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingVenueId,
    selectedVenue,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setFilters,
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // UI state management
    setSelectedVenue,

    // Error handling
    lastError,
    clearError,
  } = useVenuesContext();

  // Local UI state
  const [formModalOpened, setFormModalOpened] = useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [venueToDelete, setVenueToDelete] = useState<Venue | null>(null);
  const [searchValue, setSearchValue] = useState<string>("");

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      // Apply search filter
      const newFilters: any = {};
      if (value.trim()) {
        newFilters.name = value.trim();
      }

      // Update filters and reload data
      setFilters(newFilters);
      list(newFilters, { page: 1, pageSize: 100 }, sortBy, true);
    },
    [sortBy, setFilters, list]
  );

  // Handle add venue button click
  const handleAddVenue = useCallback(() => {
    setSelectedVenue(null);
    setFormModalOpened(true);
  }, [setSelectedVenue]);

  // Handle edit venue
  const handleEditVenue = useCallback(
    (venue: Venue) => {
      setSelectedVenue(venue);
      setFormModalOpened(true);
    },
    [setSelectedVenue]
  );

  // Handle view venue (navigate to venue details page)
  const handleViewVenue = useCallback((venue: Venue) => {
    if (venue.id) {
      // TODO: Implement venue details page
      console.log("View venue:", venue);
    }
  }, []);

  // Handle go to rooms (navigate to venue rooms page)
  // const handleGoToRooms = useCallback(
  //   (venue: Venue) => {
  //     if (venue.id) {
  //       navigate(`/venues/${venue.id}/rooms`);
  //     }
  //   },
  //   [navigate]
  // );

  // Handle remove venue button click
  const handleRemoveVenueClick = useCallback((venue: Venue) => {
    setVenueToDelete(venue);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (venueToDelete?.id) {
      const success = await remove(venueToDelete.id);
      if (success) {
        setDeleteModalOpened(false);
        setVenueToDelete(null);
      }
    }
  }, [venueToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setVenueToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setFormModalOpened(false);
    setSelectedVenue(null);
    clearError();
  }, [setSelectedVenue, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle venue creation
  const handleCreateVenue = useCallback(
    async (venueData: any) => {
      const result = await create(venueData);
      if (result) {
        setFormModalOpened(false);
        setSelectedVenue(null);
      }
      return result;
    },
    [create, setSelectedVenue]
  );

  // Handle venue update
  const handleUpdateVenue = useCallback(
    async (venueId: string, venueData: any) => {
      const result = await update(venueId, venueData);
      if (result) {
        setFormModalOpened(false);
        setSelectedVenue(null);
      }
      return result;
    },
    [update, setSelectedVenue]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-venue"
                  iconName="Plus"
                  onClick={handleAddVenue}
                  disabled={isCreating || !!removingVenueId || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingVenueId || loading}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search venues..."
                  value={searchValue}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Venues table */}
        <VenuesTable
          venues={venues}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditVenue={handleEditVenue}
          onRemoveVenue={handleRemoveVenueClick}
          onViewVenue={handleViewVenue}
          //onGoToRooms={handleGoToRooms}
          removingVenueId={removingVenueId}
          size="md"
        />

        {/* Empty state when no venues */}
        <EmptyState
          display={isEmpty && !loading}
          title="No venues yet"
          description="Start by adding your first venue to manage your locations and rooms"
          actionIcon="Plus"
          actionLabel="Add Venue"
          onAction={handleAddVenue}
        />
      </Stack>

      {/* Venue form modal for create/edit */}
      <VenueFormModal
        opened={formModalOpened}
        onClose={handleFormModalClose}
        venue={selectedVenue}
        onCreateVenue={handleCreateVenue}
        onUpdateVenue={handleUpdateVenue}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Venue"
        message={`Are you sure you want to delete "${venueToDelete?.name}"?`}
        details="This action cannot be undone. All associated rooms and bookings will also be deleted."
        confirmInProgress={removingVenueId === venueToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
